import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Clapperboard, Image as ImagenIcono, Plus, Trash2, Upload } from 'lucide-react';
import { api, apiMultipart } from '../../lib/api.ts';
import { Aviso, Boton, BotonCirculo, Campo, Cargando, EncabezadoPagina, Insignia, Selector, Tarjeta } from '../../componentes/ui.tsx';
import { CampoColor, LogoMarca, subirLogo, type Marca } from './Marcas.tsx';
import { ModalEditarProducto, type ProductoAdmin } from './Productos.tsx';
import { ModalImportar } from '../../componentes/Importar.tsx';

interface Material {
  id: string;
  titulo: string;
  tipo: 'IMAGEN' | 'VIDEO';
  marcaId: string;
  referencia: string | null;
  url: string;
}

export function PaginaAdminMarca() {
  const { marcaId } = useParams({ from: '/admin/marcas/$marcaId' });
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [aviso, setAviso] = useState<string | null>(null);
  const [tab, setTab] = useState<'productos' | 'materiales'>('productos');
  const [editando, setEditando] = useState<ProductoAdmin | null>(null);
  const [borrando, setBorrando] = useState(false);
  const [creandoProducto, setCreandoProducto] = useState(false);
  const [creandoMaterial, setCreandoMaterial] = useState(false);
  const [importandoMateriales, setImportandoMateriales] = useState(false);

  const marcas = useQuery({
    queryKey: ['admin-marcas'],
    queryFn: () => api<{ items: Marca[] }>('/admin/marcas'),
  });
  const marca = marcas.data?.items.find((m) => m.id === marcaId) ?? null;

  const productos = useQuery({
    queryKey: ['admin-productos-marca', marcaId],
    queryFn: () => api<{ items: ProductoAdmin[] }>(`/admin/productos?marca=${marcaId}&limit=100`),
  });

  const materiales = useQuery({
    queryKey: ['admin-materiales'],
    queryFn: () => api<{ items: Material[] }>('/admin/materiales'),
  });
  const materialesMarca = materiales.data?.items.filter((m) => m.marcaId === marcaId) ?? [];

  // Formulario editable, sincronizado cuando llega la marca.
  const [nombre, setNombre] = useState('');
  const [colorPrimario, setColorPrimario] = useState('#111225');
  const [colorSecundario, setColorSecundario] = useState('#d7f75b');
  useEffect(() => {
    if (marca) {
      setNombre(marca.nombre);
      setColorPrimario(marca.colorPrimario ?? '#111225');
      setColorSecundario(marca.colorSecundario ?? '#d7f75b');
    }
  }, [marca?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- resincronizar sólo al cambiar de marca

  const recargar = () => qc.invalidateQueries({ queryKey: ['admin-marcas'] });

  const guardar = useMutation({
    mutationFn: () =>
      api(`/admin/marcas/${marcaId}`, {
        metodo: 'PATCH',
        cuerpo: { nombre: nombre.trim(), colorPrimario, colorSecundario },
      }),
    onSuccess: async () => {
      setAviso('Cambios guardados.');
      await recargar();
    },
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  const cambiarLogo = useMutation({
    mutationFn: async (archivo: File) => {
      const logoAssetId = await subirLogo(archivo);
      await api(`/admin/marcas/${marcaId}`, { metodo: 'PATCH', cuerpo: { logoAssetId } });
    },
    onSuccess: () => recargar(),
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  // ── Variantes del logo ──
  const variantes = useQuery({
    queryKey: ['marca-logos', marcaId],
    queryFn: () => api<{ items: { id: string; nombre: string; url: string | null }[] }>(`/admin/marcas/${marcaId}/logos`),
  });

  const agregarVariante = useMutation({
    mutationFn: async (archivo: File) => {
      const assetId = await subirLogo(archivo);
      const nombre = archivo.name.replace(/\.[^.]+$/, '').slice(0, 60) || 'Variante';
      await api(`/admin/marcas/${marcaId}/logos`, { metodo: 'POST', cuerpo: { assetId, nombre } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marca-logos', marcaId] }),
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  const borrarVariante = useMutation({
    mutationFn: (logoId: string) => api(`/admin/marcas/logos/${logoId}`, { metodo: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marca-logos', marcaId] }),
  });

  const alternarEstado = useMutation({
    mutationFn: () =>
      api(`/admin/marcas/${marcaId}`, {
        metodo: 'PATCH',
        cuerpo: { estado: marca?.estado === 'ACTIVO' ? 'INACTIVO' : 'ACTIVO' },
      }),
    onSuccess: () => recargar(),
    onError: (e) => setAviso(`Error: ${e.message}`),
  });

  if (marcas.isLoading) return <Cargando texto="Cargando marca…" />;
  if (!marca) {
    return (
      <main className="mx-auto w-full max-w-[1600px] space-y-4">
        <Aviso>La marca no existe.</Aviso>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        titulo={marca.nombre}
        subtitulo={`${marca.codigoExterno}${marca.estado !== 'ACTIVO' ? ' · inactiva' : ''}`}
        acciones={
          <BotonCirculo aria-label="Volver a marcas" className="h-11 w-11" onClick={() => void navegar({ to: '/admin/marcas' })}>
            <ArrowLeft size={18} />
          </BotonCirculo>
        }
      />

      {aviso ? <Aviso tono={aviso.startsWith('Error') ? 'error' : 'ok'}>{aviso}</Aviso> : null}

      {/* Datos de la marca en una sola fila arriba */}
      <Tarjeta className="p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-3">
            <LogoMarca marca={marca} className="h-16 w-16" />
            <label className="block">
              <span className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-full bg-superficie2 px-4 text-sm font-semibold">
                <Upload size={15} /> {marca.logoUrl ? 'Cambiar logo' : 'Subir logo'}
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const archivo = e.target.files?.[0];
                  if (archivo) cambiarLogo.mutate(archivo);
                }}
              />
            </label>
          </div>

          <div className="min-w-56 flex-1">
            <Campo etiqueta="Nombre de la marca" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </div>
          <CampoColor etiqueta="Color principal" valor={colorPrimario} onCambiar={setColorPrimario} />
          <CampoColor etiqueta="Color secundario" valor={colorSecundario} onCambiar={setColorSecundario} />

          <div className="ml-auto flex items-center gap-2">
            <Boton variante="peligro" type="button" onClick={() => setBorrando(true)}>
              Borrar
            </Boton>
            <Boton variante="contorno" type="button" onClick={() => alternarEstado.mutate()}>
              {marca.estado === 'ACTIVO' ? 'Desactivar' : 'Activar'}
            </Boton>
            <Boton type="button" disabled={guardar.isPending || !nombre.trim()} onClick={() => guardar.mutate()}>
              {guardar.isPending ? 'Guardando…' : 'Guardar cambios'}
            </Boton>
          </div>
        </div>

        {/* Variantes del logo: otras versiones (horizontal, blanco, isotipo…) elegibles al armar el post */}
        <div className="mt-4 border-t border-superficie2 pt-4">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-suave">
            Variantes del logo
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {(variantes.data?.items ?? []).map((v) => (
              <div key={v.id} className="relative">
                <div className="interno flex h-16 w-16 items-center justify-center overflow-hidden bg-white p-1.5">
                  {v.url ? <img src={v.url} alt={v.nombre} className="max-h-full max-w-full object-contain" /> : null}
                </div>
                <p className="mt-1 w-16 truncate text-center text-[10px] text-suave">{v.nombre}</p>
                <button
                  type="button"
                  aria-label={`Quitar ${v.nombre}`}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-negro text-[10px] text-white"
                  onClick={() => borrarVariante.mutate(v.id)}
                >
                  ×
                </button>
              </div>
            ))}
            <label className="block">
              <span className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-full bg-superficie2 px-4 text-sm font-semibold">
                <Plus size={15} /> Variante
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const archivo = e.target.files?.[0];
                  if (archivo) agregarVariante.mutate(archivo);
                  e.target.value = '';
                }}
              />
            </label>
            <p className="text-xs text-suave">El cliente elige la versión al armar su post.</p>
          </div>
        </div>
      </Tarjeta>

      {/* Productos / Materiales en tabs */}
      <Tarjeta className="p-5">
        <div className="flex flex-wrap gap-2">
          <button type="button" className="chip" data-activo={tab === 'productos'} onClick={() => setTab('productos')}>
            Productos · {productos.data?.items.length ?? 0}
          </button>
          <button type="button" className="chip" data-activo={tab === 'materiales'} onClick={() => setTab('materiales')}>
            Materiales publicitarios · {materialesMarca.length}
          </button>
          <div className="ml-auto flex gap-2">
            {tab === 'productos' ? (
              <Boton variante="contorno" tamano="sm" type="button" onClick={() => setCreandoProducto(true)}>
                <Plus size={15} /> Producto
              </Boton>
            ) : (
              <>
                <Boton variante="contorno" tamano="sm" type="button" onClick={() => setCreandoMaterial(true)}>
                  <Plus size={15} /> Material
                </Boton>
                <Boton variante="contorno" tamano="sm" type="button" onClick={() => setImportandoMateriales(true)}>
                  <Upload size={15} /> Importar
                </Boton>
              </>
            )}
          </div>
        </div>

        {tab === 'productos' ? (
          <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-7">
            {productos.data?.items.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setEditando(p)}
                  className="interno block w-full overflow-hidden text-left transition hover:-translate-y-0.5 hover:shadow-[var(--sombra)]"
                >
                  {p.imagenUrl ? (
                    <img src={p.imagenUrl} alt={p.nombre} className="aspect-square w-full bg-white object-contain" />
                  ) : (
                    <span className="flex aspect-square w-full items-center justify-center bg-superficie2 text-suave">
                      <ImagenIcono size={20} />
                    </span>
                  )}
                  <div className="p-2">
                    <p className="truncate text-sm font-medium">{p.referencia}</p>
                    <p className="truncate text-xs text-suave">{p.nombre}</p>
                    {p.estado !== 'ACTIVO' ? (
                      <div className="mt-1"><Insignia tono="alerta">INACTIVO</Insignia></div>
                    ) : null}
                  </div>
                </button>
              </li>
            ))}
            {productos.data?.items.length === 0 ? (
              <li className="col-span-full py-4 text-sm text-suave">Esta marca aún no tiene productos. Créelos con + Producto o impórtelos desde Productos.</li>
            ) : null}
          </ul>
        ) : (
          <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6">
            {materialesMarca.map((m) => (
              <li key={m.id} className="interno overflow-hidden">
                {m.tipo === 'IMAGEN' ? (
                  <img src={m.url} alt={m.titulo} className="aspect-square w-full bg-superficie2 object-cover" />
                ) : (
                  <video src={m.url} className="aspect-square w-full bg-black object-cover" muted />
                )}
                <div className="flex items-center gap-1.5 p-2.5">
                  {m.tipo === 'VIDEO' ? <Clapperboard size={14} className="shrink-0 text-suave" /> : null}
                  <p className="truncate text-sm font-medium">{m.titulo}</p>
                </div>
                {m.referencia ? <p className="px-2.5 pb-2 text-xs text-suave">Ref. {m.referencia}</p> : null}
              </li>
            ))}
            {materialesMarca.length === 0 ? (
              <li className="col-span-full py-4 text-sm text-suave">Sin materiales. Créelos con + Material o use Importar.</li>
            ) : null}
          </ul>
        )}
      </Tarjeta>

      {borrando ? (
        <ModalBorrarMarca
          marca={marca}
          numProductos={productos.data?.items.length ?? 0}
          numMateriales={materialesMarca.length}
          alCerrar={() => setBorrando(false)}
          alBorrado={async () => {
            // El borrado arrastra productos y materiales en cascada: se invalida TODO el caché.
            await qc.invalidateQueries();
            void navegar({ to: '/admin/marcas' });
          }}
          alFallar={(m) => {
            setBorrando(false);
            setAviso(`Error: ${m}`);
          }}
        />
      ) : null}

      {importandoMateriales ? (
        <ModalImportar
          tipo="MATERIALES"
          titulo="Importar materiales"
          columnas="Excel con las columnas: titulo, marca (nombre de la marca), tipo (IMAGEN o VIDEO), nombre_archivo, referencia opcional y fecha_expiracion opcional. Los archivos van dentro del ZIP."
          conZip
          alCerrar={() => setImportandoMateriales(false)}
          alAplicado={async () => {
            setImportandoMateriales(false);
            await qc.invalidateQueries({ queryKey: ['admin-materiales'] });
          }}
        />
      ) : null}

      {creandoProducto ? (
        <ModalCrearProducto
          marcaId={marcaId}
          alCerrar={() => setCreandoProducto(false)}
          alGuardar={async () => {
            setCreandoProducto(false);
            setAviso('Producto creado.');
            await qc.invalidateQueries({ queryKey: ['admin-productos-marca', marcaId] });
          }}
          alFallar={(m) => {
            setCreandoProducto(false);
            setAviso(`Error: ${m}`);
          }}
        />
      ) : null}

      {creandoMaterial ? (
        <ModalCrearMaterial
          marcaId={marcaId}
          productos={productos.data?.items ?? []}
          alCerrar={() => setCreandoMaterial(false)}
          alGuardar={async () => {
            setCreandoMaterial(false);
            setAviso('Material creado.');
            await qc.invalidateQueries({ queryKey: ['admin-materiales'] });
          }}
          alFallar={(m) => {
            setCreandoMaterial(false);
            setAviso(`Error: ${m}`);
          }}
        />
      ) : null}

      {editando ? (
        <ModalEditarProducto
          producto={editando}
          marcas={marcas.data?.items ?? []}
          alCerrar={() => setEditando(null)}
          alGuardar={async () => {
            setEditando(null);
            setAviso('Producto guardado.');
            await qc.invalidateQueries({ queryKey: ['admin-productos-marca', marcaId] });
          }}
          alFallar={(m) => setAviso(`Error: ${m}`)}
        />
      ) : null}
    </main>
  );
}

/** Confirmación destructiva: el botón Borrar queda bloqueado 3 s con cuenta regresiva visible. */
function ModalBorrarMarca({
  marca,
  numProductos,
  numMateriales,
  alCerrar,
  alBorrado,
  alFallar,
}: {
  marca: Marca;
  numProductos: number;
  numMateriales: number;
  alCerrar: () => void;
  alBorrado: () => Promise<void>;
  alFallar: (mensaje: string) => void;
}) {
  const [restante, setRestante] = useState(3);
  const [borrando, setBorrando] = useState(false);

  useEffect(() => {
    if (restante <= 0) return;
    const timer = setInterval(() => setRestante((r) => r - 1), 1000);
    return () => clearInterval(timer);
  }, [restante]);

  async function borrar() {
    setBorrando(true);
    try {
      await api(`/admin/marcas/${marca.id}`, { metodo: 'DELETE' });
      await alBorrado();
    } catch (e) {
      alFallar((e as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="tarjeta w-full max-w-md space-y-4 p-6 shadow-[var(--sombra-alta)]">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-rose-500/10 text-rose-500">
            <Trash2 size={20} />
          </span>
          <h2 className="text-[22px] font-light">¿Borrar {marca.nombre}?</h2>
        </div>
        <div className="space-y-2 text-sm text-suave">
          <p>Se borra definitivamente, junto con la marca:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Sus {numProductos} producto(s).</li>
            <li>Sus {numMateriales} material(es) publicitario(s).</li>
            <li>La visibilidad y los costos de esos productos en todos los almacenes: dejan de verlos al instante.</li>
          </ul>
          <p>El historial de pedidos se conserva. No hay vuelta atrás — para ocultarla sin perder nada use Desactivar.</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" type="button" onClick={alCerrar} disabled={borrando}>
            Cancelar
          </Boton>
          <button
            type="button"
            onClick={() => void borrar()}
            disabled={restante > 0 || borrando}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-rose-600 px-5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {borrando ? 'Borrando…' : restante > 0 ? `Borrar (${restante})` : 'Borrar definitivamente'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Alta manual de producto en la marca (la foto se normaliza cuadrada, D21). */
function ModalCrearProducto({
  marcaId,
  alCerrar,
  alGuardar,
  alFallar,
}: {
  marcaId: string;
  alCerrar: () => void;
  alGuardar: () => Promise<void>;
  alFallar: (mensaje: string) => void;
}) {
  const [referencia, setReferencia] = useState('');
  const [nombre, setNombre] = useState('');
  const [categoria, setCategoria] = useState('Calzado');
  const [tipo, setTipo] = useState('');
  const [coleccion, setColeccion] = useState('');
  const [expira, setExpira] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [guardando, setGuardando] = useState(false);

  async function guardar(evento: React.FormEvent) {
    evento.preventDefault();
    setGuardando(true);
    try {
      const creado = await api<{ id: string }>('/admin/productos', {
        metodo: 'POST',
        cuerpo: {
          marcaId,
          referencia: referencia.trim(),
          nombre: nombre.trim(),
          categoria: categoria.trim(),
          tipo: tipo.trim() || undefined,
          coleccion: coleccion.trim() || undefined,
          ...(expira ? { fechaExpiracion: new Date(`${expira}T23:59:59`).toISOString() } : {}),
        },
      });
      if (foto) {
        const forma = new FormData();
        forma.append('archivo', foto);
        await apiMultipart(`/admin/productos/${creado.id}/imagen`, forma);
      }
      await alGuardar();
    } catch (e) {
      alFallar((e as Error).message);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <form onSubmit={guardar} className="tarjeta w-full max-w-lg space-y-3 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">Nuevo producto</h2>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Referencia" value={referencia} onChange={(e) => setReferencia(e.target.value)} required autoFocus />
          <Campo etiqueta="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          <Campo etiqueta="Categoría" value={categoria} onChange={(e) => setCategoria(e.target.value)} required />
          <Campo etiqueta="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} />
          <Campo etiqueta="Colección" value={coleccion} onChange={(e) => setColeccion(e.target.value)} />
          <Campo etiqueta="Expira (vacío = nunca)" type="date" value={expira} onChange={(e) => setExpira(e.target.value)} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-principal">Foto</span>
            <span className="campo flex cursor-pointer items-center gap-2">
              <Upload size={15} className="shrink-0 text-suave" />
              <span className={`truncate text-sm ${foto ? '' : 'text-suave'}`}>{foto ? foto.name : 'Elegir…'}</span>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setFoto(e.target.files?.[0] ?? null)} />
            </span>
          </label>
        </div>
        <p className="text-xs text-suave">Sin foto, el producto queda invisible para los almacenes hasta que se le suba una.</p>
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" type="button" onClick={alCerrar}>Cancelar</Boton>
          <Boton type="submit" disabled={guardando || !referencia.trim() || !nombre.trim()}>
            {guardando ? 'Creando…' : 'Crear producto'}
          </Boton>
        </div>
      </form>
    </div>
  );
}

/** Alta manual de material publicitario: sube el archivo y crea la fila. */
function ModalCrearMaterial({
  marcaId,
  productos,
  alCerrar,
  alGuardar,
  alFallar,
}: {
  marcaId: string;
  productos: ProductoAdmin[];
  alCerrar: () => void;
  alGuardar: () => Promise<void>;
  alFallar: (mensaje: string) => void;
}) {
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState<'IMAGEN' | 'VIDEO'>('IMAGEN');
  /** Código tomado del nombre del archivo (editable para corregir un nombre mal puesto). */
  const [codigo, setCodigo] = useState('');
  const [expira, setExpira] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Match en vivo contra los códigos de la marca: define si es de código o general.
  const match = codigo.trim()
    ? (productos.find((p) => p.referencia.toLowerCase() === codigo.trim().toLowerCase()) ?? null)
    : null;

  async function guardar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!archivo) return;
    setGuardando(true);
    try {
      const forma = new FormData();
      forma.append('tipo', tipo);
      forma.append('archivo', archivo);
      const asset = await apiMultipart<{ assetId: string }>('/admin/media', forma);
      await api('/admin/materiales', {
        metodo: 'POST',
        cuerpo: {
          marcaId,
          tipo,
          titulo: titulo.trim(),
          assetId: asset.assetId,
          ...(match ? { productoId: match.id } : {}),
          ...(expira ? { fechaExpiracion: new Date(`${expira}T23:59:59`).toISOString() } : {}),
        },
      });
      await alGuardar();
    } catch (e) {
      alFallar((e as Error).message);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <form onSubmit={guardar} className="tarjeta w-full max-w-lg space-y-3 p-6 shadow-[var(--sombra-alta)]">
        <h2 className="text-[22px] font-light">Nuevo material publicitario</h2>
        <Campo etiqueta="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)} required autoFocus />
        <div className="grid grid-cols-2 gap-3">
          <Selector etiqueta="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value as 'IMAGEN' | 'VIDEO')}>
            <option value="IMAGEN">Imagen</option>
            <option value="VIDEO">Video</option>
          </Selector>
          <Campo etiqueta="Expira (vacío = nunca)" type="date" value={expira} onChange={(e) => setExpira(e.target.value)} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-principal">Archivo</span>
            <span className="campo flex cursor-pointer items-center gap-2">
              <Upload size={15} className="shrink-0 text-suave" />
              <span className={`truncate text-sm ${archivo ? '' : 'text-suave'}`}>{archivo ? archivo.name : 'Elegir…'}</span>
              <input
                type="file"
                accept={tipo === 'VIDEO' ? 'video/mp4,video/*' : 'image/*'}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setArchivo(f);
                  // el código sale del nombre del archivo; editable abajo si vino mal
                  if (f) setCodigo(f.name.replace(/\.[^.]+$/, ''));
                }}
              />
            </span>
          </label>
          <div>
            <Campo
              etiqueta="Código (del nombre del archivo)"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="513039-1.PY"
            />
            <p className={`mt-1 text-xs ${match ? 'text-emerald-600' : 'text-suave'}`}>
              {match
                ? `Coincide con el producto ${match.referencia}: sólo lo verán quienes lo compraron.`
                : codigo.trim()
                  ? 'No coincide con ningún código de la marca: será general (lo ven todos los que tengan la marca).'
                  : 'Sin código: material general de la marca.'}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Boton variante="secundario" type="button" onClick={alCerrar}>Cancelar</Boton>
          <Boton type="submit" disabled={guardando || !titulo.trim() || !archivo}>
            {guardando ? 'Subiendo…' : 'Crear material'}
          </Boton>
        </div>
      </form>
    </div>
  );
}
